<template>
  <div v-if="orderStore.loading" class="flex justify-center items-center min-h-[400px]">
    <LayupSpinner />
  </div>
  <div v-else-if="orderStore.error" class="flex justify-center items-center min-h-[400px]">
    <div class="text-center">
      <p class="text-error-600 text-lg mb-4">{{ orderStore.error }}</p>
      <Button @click="orderStore.getOrderById(orderId)" variant="outline">Try Again?</Button>
    </div>
  </div>
  <div v-else>
    <div class="rounded-lg shadow-md border-x border-t border-grey-light-200 mt-0 bg-white">
      <div class="flex flex-col lg:flex-row items-center p-4">
        <div class="w-full">
          <div class="flex flex-col lg:flex-row lg:items-center gap-3">
            <Button @click="router.back()" variant="outline">
              <ArrowLeft class="h-4 w-4" />
            </Button>
            <div>
              <h1 class="text-lg font-semibold">{{ orderStore.order?.name }} - {{ orderStore.order?.reference }}</h1>
              <h2 class="text-sm">
                <span class="font-medium">{{ paymentPlan?.benefactor?.name }}</span>
              </h2>
            </div>
            <div class="flex flex-col gap-2 mt-2 lg:flex-row lg:mt-0 lg:ml-auto">
              <Button
                v-if="orderStore.order?.state !== 'CANCELLING' && orderStore.order?.state !== 'CANCELLED'"
                @click="onShowCancellationPolicy"
                variant="outline"
              >
                <Info class="h-4 w-4" />
                Cancellation Policy
              </Button>
              <Button
                v-if="
                  orderStore.order?.state !== 'CANCELLING' &&
                  orderStore.order?.state !== 'CANCELLED' &&
                  orderStore.order?.state !== 'COMPLETED' &&
                  merchantStore.merchant.features?.amendmentPreview
                "
                @click="onCreateAmendmentPlan"
                variant="outline"
              >
                <Edit class="h-4 w-4" />
                {{ !orderStore.order?.amendmentPreviewId ? 'Amend Plan' : 'New Amendment' }}
              </Button>
              <Button
                v-if="
                  orderStore.order?.state !== 'CANCELLING' &&
                  orderStore.order?.state !== 'CANCELLED' &&
                  orderStore.order?.state !== 'COMPLETED' &&
                  !!orderStore.order?.amendmentPreviewId &&
                  !amendmentPreviewAccepted
                "
                @click="onConfirmAmendmentPlan"
                variant="outline"
              >
                <Edit class="h-4 w-4" />
                Confirm Amendment
              </Button>
              <Button
                v-if="
                  orderStore.order?.state !== 'CANCELLING' &&
                  orderStore.order?.state !== 'CANCELLED' &&
                  orderStore.order?.state !== 'COMPLETED' &&
                  !!paymentPlan
                "
                @click="onShowDirectPayment"
                variant="primary"
              >
                <Banknote class="h-4 w-4" />
                Direct Payment
              </Button>
              <Button v-if="orderStore.order?.state === 'SUSPENDED'" @click="onShowActivateModal" variant="primary">
                <CircleCheck class="h-4 w-4" />
                Activate Order
              </Button>
              <Button v-if="orderStore.order?.state === 'SUSPENDED'" @click="onShowCancelModal" variant="primary">
                <CircleX class="h-4 w-4" />
                Cancel Order
              </Button>
            </div>
          </div>
          <div class="w-full mt-4">
            <div class="w-full h-2 bg-grey-light-200 rounded-full overflow-hidden">
              <div
                class="h-full bg-brand-600 rounded-full transition-all duration-300"
                :style="{ width: paymentProgress?.percent + '%' }"
              ></div>
            </div>
            <div class="flex items-center justify-between text-sm text-brand-600 mt-2">
              <span class="font-medium">{{ paymentProgress?.label }}</span>
              <span class="font-medium">{{ paymentProgress?.percent }}%</span>
            </div>
          </div>
          <div class="w-full mt-6">
            <div
              class="grid grid-cols-1 gap-2 md:grid-cols-2"
              :class="useTravelDate ? 'xl:grid-cols-5' : 'xl:grid-cols-4'"
            >
              <div v-if="nextPayment(paymentPlan)" class="col-span-1">
                <div class="flex flex-col">
                  <h3 class="text-xs text-grey-light-600 font-medium">
                    {{ paymentPlan?.frequency === 'WEEKLY' ? 'Weekly' : 'Monthly' }} Amount
                  </h3>
                  <span class="text-brand-600 text-lg font-semibold">{{
                    displayCurrency(nextPayment(paymentPlan)?.amount!)
                  }}</span>
                </div>
              </div>
              <div class="col-span-1">
                <div class="flex flex-col">
                  <h3 class="text-xs text-grey-light-600 font-medium">Total Order Value</h3>
                  <span class="text-brand-600 text-lg font-semibold">{{
                    displayCurrency(planAmount(paymentPlan))
                  }}</span>
                </div>
              </div>
              <div class="col-span-1">
                <div class="flex flex-col">
                  <h3 class="text-xs text-grey-light-600 font-medium">Paid To Date</h3>
                  <span class="text-brand-600 text-lg font-semibold">
                    {{ displayCurrency(contributions(paymentPlan)) }}
                  </span>
                </div>
              </div>
              <div class="col-span-1">
                <div class="flex flex-col">
                  <h3 class="text-xs text-grey-light-600 font-medium">Balance</h3>
                  <span class="text-brand-600 text-xl font-bold">
                    {{ displayCurrency(outstanding(paymentPlan)) }}
                  </span>
                </div>
              </div>
              <div
                :class="
                  (useTravelDate ? 'col-span-5' : 'col-span-4') +
                  ' hidden h-[2px] bg-grey-light-200 my-2 xl:inline-block'
                "
              ></div>
              <div v-if="nextPayment(paymentPlan)" class="col-span-1">
                <div class="flex flex-col">
                  <h3 class="text-xs text-grey-light-600 font-medium">Next Instalment</h3>
                  <span class="text-brand-600 text-lg font-semibold">
                    {{ formatDate(nextPayment(paymentPlan)?.dueAt) }}
                  </span>
                </div>
              </div>
              <div class="col-span-1">
                <Popover v-model:open="showDatePicker">
                  <PopoverTrigger v-if="isAmendEnabled" as-child>
                    <div class="flex flex-col hover:cursor-pointer">
                      <h3 class="flex items-center text-xs text-grey-light-600 font-medium">
                        Final Payment Date
                        <SquarePen v-if="!useTravelDate" class="h-4 w-4 ml-2" />
                      </h3>
                      <span class="text-brand-600 text-lg font-semibold">
                        {{ formatDate(orderStore.order?.endDateMax) }}
                      </span>
                    </div>
                  </PopoverTrigger>
                  <div v-else class="flex flex-col">
                    <h3 class="flex items-center text-xs text-grey-light-600 font-medium">Final Payment Date</h3>
                    <span class="text-brand-600 text-lg font-semibold">
                      {{ formatDate(orderStore.order?.endDateMax) }}
                    </span>
                  </div>

                  <PopoverContent class="w-auto">
                    <Calendar v-model="date" :minValue="originalDate" :weekday-format="'short'" class="rounded-md" />
                    <Button type="button" class="mt-2" @click="onUpdateEndDate">
                      <LayupSpinner v-if="isLoading" :size="4" />
                      <span v-else>Submit</span>
                    </Button>
                  </PopoverContent>
                </Popover>
              </div>
              <div class="col-span-1">
                <div class="flex flex-col">
                  <h3 class="text-xs text-grey-light-600 font-medium">Duration</h3>
                  <span class="text-brand-600 text-lg font-semibold">{{ duration(paymentPlan) }}</span>
                </div>
              </div>
              <div v-if="useTravelDate" class="col-span-1">
                <div class="flex flex-col">
                  <h3 class="flex items-center text-xs text-grey-light-600 font-medium">Travel Date</h3>
                  <span class="text-brand-600 text-lg font-semibold">
                    {{ formatDate(orderStore.order?.travelDate) }}
                  </span>
                </div>
              </div>
              <div v-if="!isAmendEnabled" class="col-span-1">
                <div class="flex flex-col">
                  <h3 class="text-xs text-grey-light-600 font-medium">Order Status</h3>
                  <span class="text-brand-600 text-lg font-semibold">
                    <Badge :variant="getOrderStatusVariant(orderStore.order?.state)">
                      {{ orderStore.order?.state }}
                    </Badge>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="flex flex-col gap-4 my-4">
      <ProductsTable :order="orderStore.order" :can-edit="isAmendEnabled" />
      <StatementTable
        :order="orderStore.order"
        :order-state="orderStore.order?.state ?? ''"
        :payments="paymentReports(orderStore.order?.state ?? '', paymentPlan)"
      />
      <div v-if="orderStore.order?.notifications && orderStore.order?.notifications.length > 0">
        <NotificationsTable :notifications="orderStore.order?.notifications" :order="orderStore.order" />
      </div>
    </div>

    <CancellationPolicyModal
      :show="showCancellation"
      :order="orderStore.order"
      @update:show="showCancellation = false"
    />
    <ActivationModal :show="showActivation" :order="orderStore.order" @update:show="showActivation = false" />
    <CancellationModal
      :show="showOrderCancellation"
      :order="orderStore.order"
      @update:show="showOrderCancellation = false"
    />
    <AmendmentModel
      :show="showAmendmentPlan"
      :order="orderStore.order"
      :is-creating="isCreatingAmendmentPlan"
      @update:show="showAmendmentPlan = $event"
      @refresh-order="onRefreshOrderAfterAmendment"
    />
    <DirectPaymentModal :show="showDirectPayment" :order="orderStore.order" @update:show="showDirectPayment = false" />
  </div>
</template>

<script setup lang="ts">
import LayupSpinner from '@/components/common/LayupSpinner.vue';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { NetworkError } from '@/errors';
import { OrderState } from '@/models/orders';
import { UserRole } from '@/models/users';
import { getAmendmentPreviewById } from '@/services/amendment';
import { getOrderByIdWithPlansPaymentsAndConnectors } from '@/services/orders';
import { useMerchantStore } from '@/stores/merchants';
import { useOrderStore } from '@/stores/orders';
import { useUserStore } from '@/stores/users';
import { displayCurrency, formatDate } from '@/utils/format.utils';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import { getLocalTimeZone, parseAbsolute, toCalendarDate, today, type DateValue } from '@internationalized/date';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { ArrowLeft, Banknote, CircleCheck, CircleX, Edit, Info, SquarePen } from 'lucide-vue-next';
import { computed, onMounted, ref, watch, type Ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useToast } from 'vue-toast-notification';
import type { ModelPaymentPlanResponse } from '@layup/api-sdk';
import { getOrderStatusVariant, UpdateOrderEndDate } from '../../../../utils/order.utils';
import {
  contributions,
  duration,
  nextPayment,
  outstanding,
  paymentReports,
  paymentsRemaining,
  percentageComplete,
  planAmount,
} from '../../../../utils/paymentPlan.utils';
import ActivationModal from './ActivationModal.vue';
import AmendmentModel from './AmendmentModel.vue';
import CancellationModal from './CancellationModal.vue';
import CancellationPolicyModal from './CancellationPolicyModal.vue';
import DirectPaymentModal from './direct-payment-modal/DirectPaymentModal.vue';
import NotificationsTable from './tables/NotificationsTable.vue';
import ProductsTable from './tables/ProductsTable.vue';
import StatementTable from './tables/StatementTable.vue';

dayjs.extend(advancedFormat);

const orderStore = useOrderStore();
const userStore = useUserStore();
const merchantStore = useMerchantStore();
const toast = useToast();
const router = useRouter();
const route = useRoute();
const orderId = route.params.id as string;
const paymentPlan = ref<ModelPaymentPlanResponse>();
const isLoading = ref(false);
const showCancellation = ref(false);
const showOrderCancellation = ref(false);
const showActivation = ref(false);
const showAmendmentPlan = ref(false);
const isCreatingAmendmentPlan = ref(false);
const showDirectPayment = ref(false);
const showDatePicker = ref(false);
const amendmentPreviewAccepted = ref(false);
const useTravelDate = computed(() => {
  return merchantStore.merchant.features?.enableTravelDate || false;
});
const originalDate = ref<DateValue>();
const originalTravelDate = ref<DateValue>();
const date = ref<DateValue>() as Ref<DateValue>;

onMounted(() => {
  watch(
    () => orderStore.order,
    async (order) => {
      paymentPlan.value = orderStore.getPaymentPlan();

      let newDate: DateValue;
      if (!order?.endDateMax) {
        newDate = today(getLocalTimeZone());
      } else {
        const isoDate = new Date(order.endDateMax).toISOString();
        newDate = toCalendarDate(parseAbsolute(isoDate, getLocalTimeZone()));
      }
      const newTravelDate = !order?.travelDate
        ? today(getLocalTimeZone())
        : toCalendarDate(parseAbsolute(new Date(order.travelDate).toISOString(), getLocalTimeZone()));

      date.value = newDate;
      originalDate.value = newDate;
      originalTravelDate.value = newTravelDate;

      const amendmentPreviewId = order?.amendmentPreviewId;
      if (amendmentPreviewId) {
        try {
          const preview = await getAmendmentPreviewById(amendmentPreviewId);
          amendmentPreviewAccepted.value = preview?.accepted || false;
        } catch {
          amendmentPreviewAccepted.value = false;
        }
      }
    },
    { immediate: true },
  );
});

onMounted(async () => {
  await orderStore.getOrderById(orderId);
});

const paymentProgress = computed(() => {
  const verb = paymentPlan.value?.frequency === 'WEEKLY' ? 'week' : 'month';
  const remaining = paymentsRemaining(paymentPlan.value);
  if (remaining <= 0) {
    return {
      percent: 100,
      label: 'Complete',
    };
  }
  return {
    percent: percentageComplete(paymentPlan.value),
    label: `${remaining} ${remaining === 1 ? verb : verb + 's'} remaining`,
  };
});

const isAmendEnabled = computed(() => {
  if (
    merchantStore.merchant.features?.enableAmendmentApi &&
    userStore.user.merchantRole === UserRole.ADMIN &&
    (orderStore.order?.state === OrderState.PLACED || orderStore.order?.state === OrderState.SUSPENDED) &&
    !useTravelDate.value
  ) {
    return true;
  }
  return false;
});

const onShowCancellationPolicy = () => {
  showCancellation.value = true;
};

const onShowDirectPayment = () => {
  showDirectPayment.value = true;
};

const onUpdateEndDate = async () => {
  const selectedDate = date.value?.toString();
  const originalDateValue = originalDate.value?.toString();
  if (selectedDate !== originalDateValue) {
    try {
      isLoading.value = true;
      await UpdateOrderEndDate(orderStore.order?._id || '', selectedDate, originalTravelDate.value?.toString() || '');
      showDatePicker.value = false;
      isLoading.value = false;
      const updatedOrder = await getOrderByIdWithPlansPaymentsAndConnectors(orderStore.order?._id!);
      orderStore.updateOrder(updatedOrder);
      addMetric('order-details', { status: 'success', channel: 'merchant' });
    } catch (error) {
      if (error instanceof NetworkError) {
        createWarning(error);
        addMetric('order-details', { status: 'network_error', channel: 'merchant' });
      } else {
        createException(error);
        addMetric('order-details', { status: 'error', channel: 'merchant' });
      }
      toast.error('Failed to update order end date.', {
        position: 'top',
      });
    }
  }
};

const onShowCancelModal = () => {
  showOrderCancellation.value = true;
};
const onShowActivateModal = () => {
  showActivation.value = true;
};

const onCreateAmendmentPlan = () => {
  isCreatingAmendmentPlan.value = true;
  showAmendmentPlan.value = true;
};

const onConfirmAmendmentPlan = () => {
  isCreatingAmendmentPlan.value = false;
  showAmendmentPlan.value = true;
};

const onRefreshOrderAfterAmendment = async () => {
  await orderStore.getOrderById(orderId);
};
</script>
